import { getAppointmentDisplayMeta } from './appointmentWorkflow';

const ONLINE_PAYMENT_GATEWAYS = new Set(['authorize']);

/** True when the appointment uses an online payment gateway. */
export const usesOnlinePaymentGateway = (appointment) => {
  if (!appointment) return false;
  const method = String(
    appointment.paymentMethod ||
    appointment.paymentDetails?.paymentMethod ||
    appointment.paymentDetails?.provider ||
    ''
  )
    .trim()
    .toLowerCase();
  return ONLINE_PAYMENT_GATEWAYS.has(method);
};

/** Staff may manually confirm payment only for non-gateway (e.g. cash) bookings. */
export const canStaffManuallyMarkAppointmentPaid = (appointment) => {
  if (!appointment || usesOnlinePaymentGateway(appointment)) return false;
  const status = String(appointment.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'no show' || status === 'completed') return false;
  return appointment.paymentStatus !== 'paid';
};

const isAppointmentFullyPaid = (appointment) => {
  const paymentStatus = String(appointment?.paymentStatus || '').toLowerCase();
  return (
    paymentStatus === 'paid' ||
    appointment?.paymentDetails?.status === 'paid' ||
    appointment?.balance?.status === 'paid' ||
    appointment?.paymentDetails?.balance?.status === 'paid'
  );
};

const appointmentHasPaymentTracking = (appointment) => {
  if (!appointment) return false;

  const totalPrice = parseFloat(appointment.totalPrice || 0);
  if (Number.isFinite(totalPrice) && totalPrice > 0) return true;

  return Boolean(
    appointment.paymentStatus ||
      appointment.depositStatus ||
      appointment.depositPaid ||
      appointment.paymentMethod ||
      appointment.paymentDetails
  );
};

/** Compact payment pill for overview appointment cards. */
export const getAppointmentPaymentCardMeta = (appointment) => {
  if (!appointment || !appointmentHasPaymentTracking(appointment)) return null;

  const status = String(appointment.status || '').toLowerCase();
  if (status === 'cancelled') return null;

  if (isAppointmentFullyPaid(appointment)) {
    return { label: 'Paid', cssClass: 'paid' };
  }

  if (status === 'pending_payment' || status === 'processing') {
    return null;
  }

  return { label: 'Pending Payment', cssClass: 'pending-payment' };
};

// Status color mapping with solid colors
export const getStatusColor = (status) => {
  const statusColors = {
    'cancelled': '#f44336',    // Red
    'completed': '#4caf50',    // Green
    'confirmed': '#4caf50',    // Green (confirmed = good to go)
    'pending': '#ff9800',      // Orange
    'pending_payment': '#f44336', // Red
    'checked_in': '#00897B',
    'rescheduled': '#2196f3',  // Blue
    'no show': '#9c27b0',      // Purple
    'default': '#757575'       // Grey
  };

  return statusColors[status?.toLowerCase()] || statusColors.default;
};

// Get background color (return solid color directly)
export const getStatusBackgroundColor = (statusOrAppointment) => {
  if (statusOrAppointment && typeof statusOrAppointment === 'object') {
    const { colorKey } = getAppointmentDisplayMeta(statusOrAppointment);
    return getStatusColor(colorKey);
  }
  return getStatusColor(statusOrAppointment);
};

// Get contrasting text color (always white for solid backgrounds)
export const getContrastTextColor = () => '#ffffff';

// Helper function to get status label (pass appointment object as first arg for full meta)
export const getStatusLabel = (statusOrAppointment, appointment) => {
  if (statusOrAppointment && typeof statusOrAppointment === 'object' && !appointment) {
    return getAppointmentDisplayMeta(statusOrAppointment).label;
  }
  const apt = appointment || null;
  const status = typeof statusOrAppointment === 'string' ? statusOrAppointment : apt?.status;
  if (apt) {
    return getAppointmentDisplayMeta({ ...apt, status: status || apt.status }).label;
  }
  if (!status) return '';
  return getAppointmentDisplayMeta({ status }).label;
};

export const isAppointmentCompleted = (appointment) =>
  String(appointment?.status || '').trim().toLowerCase() === 'completed';

/** Chronological order with completed appointments grouped at the bottom. */
export const sortAppointmentsByDateTimeCompletedLast = (appointments, getDateTime) => {
  if (!appointments?.length) return [];

  return [...appointments].sort((a, b) => {
    const aCompleted = isAppointmentCompleted(a);
    const bCompleted = isAppointmentCompleted(b);

    if (aCompleted !== bCompleted) {
      return aCompleted ? 1 : -1;
    }

    const aDate = getDateTime(a?.dateTime);
    const bDate = getDateTime(b?.dateTime);
    return (aDate?.getTime?.() || 0) - (bDate?.getTime?.() || 0);
  });
};

// Appointment status constants
export const APPOINTMENT_STATUSES = [
  'pending',
  'confirmed',
  'completed',
  'cancelled',
  'rescheduled',
  'no show'
];

// Status display names
export const STATUS_DISPLAY_NAMES = {
  'pending': 'Pending',
  'confirmed': 'Confirmed',
  'completed': 'Completed',
  'cancelled': 'Cancelled',
  'rescheduled': 'Rescheduled',
  'no show': 'No Show'
};